import { Controller } from "@hotwired/stimulus"

// Panel de creación y edición masiva de redirecciones.
// Maneja filas dinámicas (añadir/quitar) y la cascada Tipo de producto -> Producto.
//
// El mismo controller sirve a dos vistas con nombres de campo distintos:
//   - Alta masiva: nombres por fila con corchetes  -> redirections[i][productos_list]
//   - Edición masiva (localizador): nombres planos  -> productos_list
// Por eso #campo casa ambos esquemas.
//
// La cascada Tipo -> Producto puede vivir en dos ámbitos: dentro de una "fila"
// (localizador de edición) o en la "cabecera" global del alta masiva (un único
// Tipo + Producto para todo el lote). #ambito resuelve cualquiera de los dos.
export default class extends Controller {
  static targets = ["contenedor", "plantilla", "fila", "cabecera", "seleccionable"]

  #indice

  // Al cargar, si un ámbito ya trae un tipo elegido (localizador tras una búsqueda o
  // re-render con error), rellena su datalist sin tocar el producto ya seleccionado.
  connect() {
    [...this.filaTargets, ...this.cabeceraTargets].forEach(ambito => {
      const tipo = this.#campo(ambito, "producto_type")
      if (tipo?.value) this.#cargarOpciones(ambito, tipo.value)
    })
  }

  // Botones del listado de edición: marcar o desmarcar todas las redirecciones a la vez.
  marcarTodas() {
    this.#fijarTodas(true)
  }

  desmarcarTodas() {
    this.#fijarTodas(false)
  }

  anadirFila() {
    const html = this.plantillaTarget.innerHTML.replaceAll("__INDEX__", this.#siguienteIndice())
    this.contenedorTarget.insertAdjacentHTML("beforeend", html)
  }

  quitarFila(event) {
    const fila = event.target.closest("[data-redirecciones-masivas-target='fila']")
    if (this.filaTargets.length > 1) {
      fila.remove()
    } else {
      this.#limpiarFila(fila)
    }
  }

  tipoCambiado(event) {
    const ambito = this.#ambito(event.target)

    // Al cambiar el tipo se descarta el producto elegido previamente.
    this.#fijaValor(ambito, "productos_list", "")
    this.#revalidaProducto(ambito) // limpia producto_id y la validez pegada del campo
    this.#cargarOpciones(ambito, event.target.value)
  }

  productoCambiado(event) {
    this.#revalidaProducto(this.#ambito(event.target))
  }

  // Ámbito de la cascada: la fila (localizador) o la cabecera global (alta masiva).
  #ambito(elemento) {
    return elemento.closest(
      "[data-redirecciones-masivas-target='fila'], [data-redirecciones-masivas-target='cabecera']"
    )
  }

  #siguienteIndice() {
    this.#indice = (this.#indice ?? this.contenedorTarget.children.length - 1) + 1
    return this.#indice
  }

  // Casa tanto redirections[i][clave] (alta masiva) como clave (localizador de edición).
  #campo(fila, clave) {
    return fila.querySelector(`[name$='[${clave}]'], [name='${clave}']`)
  }

  #fijaValor(fila, clave, valor) {
    const campo = this.#campo(fila, clave)
    if (campo) campo.value = valor
  }

  // Recalcula producto_id y la validez del campo de producto a partir del texto actual.
  // Se llama al escribir (productoCambiado) y tras resetear el campo programáticamente
  // (tipoCambiado / #limpiarFila) para que el estado de invalidez nativo no se quede
  // "pegado" cuando el campo vuelve a un estado válido (vacío). Ver Trello #1917.
  #revalidaProducto(ambito) {
    const campo = this.#campo(ambito, "productos_list")
    if (!campo) return

    const datalist = ambito.querySelector("datalist")
    const opcion = Array.from(datalist?.options ?? []).find(o => o.value === campo.value)
    this.#fijaValor(ambito, "producto_id", opcion ? opcion.dataset.value : "")

    // Texto escrito que no casa con ningún producto real: no dejamos enviar con el producto
    // a medias (producto_id vacío -> clicks sin producto en los marts). Igual que el alta simple.
    const invalido = Boolean(campo.value) && !opcion
    campo.setCustomValidity(invalido ? "No es un producto válido" : "")
    if (invalido) campo.reportValidity()
  }

  #fijarTodas(valor) {
    this.seleccionableTargets.forEach(casilla => { casilla.checked = valor })
  }

  // Rellena el datalist de la fila con los productos del tipo dado. Solo toca el
  // datalist: no modifica el producto ya seleccionado (lo usa connect y tipoCambiado).
  async #cargarOpciones(fila, tipo) {
    const datalist = fila.querySelector("datalist")
    datalist.innerHTML = ""
    if (!tipo) return

    try {
      const respuesta = await fetch(`/ajax/productos/${tipo}?locale=${datalist.dataset.locale}`)
      const productos = await respuesta.json()
      productos.forEach(producto => {
        const opcion = document.createElement("option")
        opcion.value = producto.permalink
        opcion.dataset.value = producto.id
        datalist.appendChild(opcion)
      })
    } catch {
      console.error(`No se pudieron cargar los productos de tipo ${tipo}`)
    }
  }

  #limpiarFila(fila) {
    fila.querySelectorAll("input, select").forEach(campo => { campo.value = "" })
    fila.querySelector("datalist").innerHTML = ""
    this.#revalidaProducto(fila) // evita dejar la validez pegada al vaciar la fila
  }
}
